import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaStar, FaUserLock, FaStoreAlt, FaPhoneAlt, FaMapMarkerAlt,
    FaShieldAlt, FaChevronDown, FaExclamationCircle, FaSignOutAlt, FaColumns, FaMobileAlt, FaCheckCircle
} from "react-icons/fa";

import PublicBackMethods from "./PublicBackMethods";
import RatingModal from "./RatingModal";
import ComplaintModal from "./ComplaintModal";
import LoginModal from "./LoginModal";

// --- مكون نافذة التحقق من الهاتف (OTP) ---
const PhoneVerificationModal = ({ isOpen, onClose, onVerified, public_request }) => {
    const [otpSent, setOtpSent] = useState(false);
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (isOpen) {
            const savedPhone = sessionStorage.getItem("pending_otp_phone");
            if (savedPhone) setPhone(savedPhone);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSendOtp = async () => {
        if (!phone || phone.length < 10) {
            setError("يرجى إدخال رقم هاتف صحيح");
            return;
        }
        setLoading(true);
        setError("");
        try {
            await public_request.post('/public/complaints/phone/send-otp', { phoneNumber: phone });
            setOtpSent(true);
            sessionStorage.setItem("pending_otp_phone", phone);
        } catch (err) {
            setError(err.response?.data?.message || "فشل إرسال الرمز. تأكد من الرقم.");
        } finally { setLoading(false); }
    };

    const handleVerifyOtp = async () => {
        if (!code) {
            setError("يرجى إدخال رمز التحقق");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const response = await public_request.post('/public/complaints/phone/verify-otp', {
                phoneNumber: phone,
                code: code
            });

            // استخراج التوكن القادم من الباك إيند
            const tokenFromServer = response.data.phoneToken || response.data.token || code;

            sessionStorage.removeItem("pending_otp_phone");
            onVerified(phone, tokenFromServer);
        } catch (err) {
            setError(err.response?.data?.message || "رمز التحقق غير صحيح أو منتهي.");
        } finally { setLoading(false); }
    };

    const resetProcess = () => {
        setOtpSent(false);
        setPhone("");
        setCode("");
        setError("");
    };

    return (
        <div className="otp-modal-overlay">
            <div className="otp-modal-card shadow-lg animate-fade-in">
                <div className="otp-icon-header">
                    {!otpSent ? <FaMobileAlt /> : <FaCheckCircle className="text-success" />}
                </div>
                <h3 className="mb-2">التحقق من الهوية</h3>
                <p className="text-muted small mb-4">
                    {otpSent ? "أدخل الرمز المرسل إلى هاتفك" : "أدخل رقم هاتفك لتتمكن من تقديم شكوى رسمية"}
                </p>

                <div className="mb-3">
                    <input
                        type="tel"
                        placeholder="09xxxxxxxx"
                        className={`form-control otp-input mb-3 ${otpSent ? 'opacity-50' : ''}`}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={otpSent}
                    />

                    {!otpSent && (
                        <div className="d-grid gap-2">
                            <button className="btn btn-primary py-2 fw-bold" onClick={handleSendOtp} disabled={loading}>
                                {loading ? "جاري الإرسال..." : "إرسال رمز التحقق"}
                            </button>
                            <button type="button" className="btn btn-link text-info small border-0 p-0" onClick={() => setOtpSent(true)} style={{ fontSize: '12px', textDecoration: 'none' }}>
                                لدي رمز بالفعل؟ أدخله هنا
                            </button>
                        </div>
                    )}

                    {otpSent && (
                        <div className="animate-fade-in">
                            <input
                                type="text"
                                placeholder="- - - - - -"
                                className="form-control otp-input code mb-3"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                autoFocus
                            />
                            <button className="btn btn-success w-100 py-2 fw-bold" onClick={handleVerifyOtp} disabled={loading}>
                                {loading ? "جاري التحقق..." : "تأكيد والبدء بالشكوى"}
                            </button>
                            <button className="btn btn-link text-info small w-100 mt-2" onClick={resetProcess}>تعديل الرقم؟</button>
                        </div>
                    )}
                </div>
                {error && <div className="otp-error-msg mb-3">{error}</div>}
                <button className="btn btn-link text-white-50 w-100" onClick={onClose}>إلغاء</button>
            </div>
        </div>
    );
};

export default function PublicDashboard() {
    const { public_request, savePublicLogin, accountToken, establishmentUid, publicLogout } = PublicBackMethods();
    const navigate = useNavigate();
    const { id } = useParams();
    const [establishment, setEstablishment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
    const [verifiedPhone, setVerifiedPhone] = useState("");
    const [phoneToken, setPhoneToken] = useState("");

    const sectionRef = useRef(null);
    const isOwner = useMemo(() => accountToken && establishmentUid === id, [accountToken, establishmentUid, id]);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const response = await public_request.get(`/public/establishments/${id}`);
                setEstablishment(response.data);
            } catch (error) { console.error("Error:", error); }
            finally { setLoading(false); }
        };
        if (id) fetchDetails();
    }, [id, public_request]);

    const toggleSection = (section) => {
        if (activeSection === section) {
            setActiveSection(null);
        } else {
            if (section === 'complaint' && !phoneToken) {
                setIsPhoneModalOpen(true);
                return;
            }
            setActiveSection(section);
            setTimeout(() => { sectionRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
        }
    };

    const menuItems = useMemo(() => {
        if (isOwner) {
            return [
                { id: 1, title: "بيانات الفعالية", subtitle: "الضبوط والزيارات", icon: <FaColumns />, gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", action: () => navigate(`/establishment-dashboard/${id}`) },
                { id: 5, title: "خروج", subtitle: "إنهاء الجلسة", icon: <FaSignOutAlt />, gradient: "linear-gradient(135deg, #232526 0%, #414345 100%)", action: () => publicLogout() }
            ];
        } else {
            return [
                { id: 2, title: "تقديم شكوى", subtitle: "حماية المستهلك", icon: <FaExclamationCircle />, gradient: "linear-gradient(135deg, #f85032 0%, #e73827 100%)", action: () => toggleSection('complaint') },
                { id: 3, title: "تقييم الفعالية", subtitle: "شاركنا تجربتك", icon: <FaStar />, gradient: "linear-gradient(135deg, #f1b400 0%, #f18a00 100%)", action: () => toggleSection('rating') },
                { id: 4, title: "تسجيل الدخول", subtitle: "لأصحاب المنشآت", icon: <FaUserLock />, gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", action: () => setIsLoginModalOpen(true) }
            ];
        }
    }, [isOwner, id, phoneToken, activeSection]);

    if (loading) return <div className="loader-wrapper" style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0f172a' }}><div className="spinner-border text-light"></div></div>;

    return (
        <div className="main-wrapper" dir="rtl">
            <div className="content-container">
                <header className="hero-section text-center pt-5">
                    <div className="gov-badge mb-4">
                        <FaShieldAlt className="ms-2" />
                        {isOwner ? "مرحباً بك، صاحب المنشأة" : "الإدارة العامة للتجارة الداخلية وحماية المستهلك"}
                    </div>
                    <div className="main-card-info shadow">
                        <div className="store-icon-wrapper"><FaStoreAlt /></div>
                        <h1 className="est-name-highlight">{establishment?.name || "تحميل..."}</h1>
                    </div>
                </header>

                <div className="container py-5">
                    <div className="row g-4 justify-content-center">
                        {menuItems.map((item) => (
                            <div key={item.id} className="col-lg-3 col-md-6">
                                <div className={`glass-card ${((activeSection === 'complaint' && item.id === 2) || (activeSection === 'rating' && item.id === 3)) ? 'active' : ''}`} onClick={item.action}>
                                    <div className="card-icon" style={{ background: item.gradient }}>{item.icon}</div>
                                    <div className="card-text"><h3>{item.title}</h3><p>{item.subtitle}</p></div>
                                    <FaChevronDown className={`chevron ${activeSection && item.id === (activeSection === 'complaint' ? 2 : 3) ? 'rotate' : ''}`} />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div ref={sectionRef} className="dynamic-content-area mt-5">
                        {activeSection === 'complaint' && (
                            <div className="section-container shadow-lg animate-fade-in">
                                <div className="section-header bg-danger text-white">تقديم شكوى رسمية</div>
                                <ComplaintModal
                                    isOpen={true}
                                    onClose={() => setActiveSection(null)}
                                    establishmentId={id}
                                    backEnd_request={public_request}
                                    verifiedPhone={verifiedPhone}
                                    phoneToken={phoneToken}
                                    onTokenInvalid={() => { setPhoneToken(""); setIsPhoneModalOpen(true); }}
                                />
                            </div>
                        )}
                        {activeSection === 'rating' && (
                            <div className="section-container shadow-lg animate-fade-in">
                                <div className="section-header bg-warning text-dark">تقييم الخدمة</div>
                                <RatingModal isOpen={true} onClose={() => setActiveSection(null)} establishmentId={id} backEnd_request={public_request} isInline={true} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} fieldUid={id} backEnd_request={public_request} savePublicLogin={savePublicLogin} />

            <PhoneVerificationModal
                isOpen={isPhoneModalOpen}
                onClose={() => setIsPhoneModalOpen(false)}
                public_request={public_request}
                onVerified={(phone, token) => { setVerifiedPhone(phone); setPhoneToken(token); setIsPhoneModalOpen(false); setActiveSection('complaint'); }}
            />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap');
                .main-wrapper { background: #0f172a; min-height: 100vh; font-family: 'Cairo', sans-serif; color: white; position: relative; }
                .main-card-info { background: rgba(255, 255, 255, 0.05); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 40px; padding: 40px; max-width: 850px; margin: 0 auto; }
                .est-name-highlight { font-size: 2.8rem; font-weight: 900; background: linear-gradient(to right, #ffffff, #94a3b8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
                .section-container { background: #1e293b; border-radius: 25px; overflow: hidden; border: 1px solid rgba(255,255,255,0.1); }
                .section-header { padding: 15px 25px; font-weight: 700; font-size: 1.2rem; text-align: center; }
                .glass-card { background: white; border-radius: 24px; padding: 20px; display: flex; align-items: center; gap: 15px; cursor: pointer; transition: 0.3s; }
                .glass-card.active { border: 2px solid #38bdf8; transform: scale(1.02); }
                .card-icon { width: 55px; height: 55px; border-radius: 16px; display: flex; justify-content: center; align-items: center; color: white; font-size: 22px; flex-shrink: 0; }
                .card-text h3 { color: #0f172a; font-size: 17px; font-weight: 800; margin: 0; }
                .card-text p { color: #64748b; font-size: 13px; margin: 0; }
                .chevron { margin-right: auto; color: #cbd5e1; transition: 0.3s; }
                .chevron.rotate { transform: rotate(180deg); }
                .store-icon-wrapper { width: 70px; height: 70px; background: #fbbf24; border-radius: 22px; display: flex; align-items: center; justify-content: center; font-size: 2rem; color: #0f172a; margin: 0 auto 15px; }
                .gov-badge { display: inline-flex; align-items: center; background: rgba(56, 189, 248, 0.1); color: #38bdf8; padding: 8px 20px; border-radius: 100px; font-size: 0.9rem; font-weight: 600; }
                .otp-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center; z-index: 3000; backdrop-filter: blur(8px); }
                .otp-modal-card { background: #1e293b; padding: 40px; border-radius: 30px; width: 90%; max-width: 400px; text-align: center; border: 1px solid rgba(255,255,255,0.1); }
                .otp-icon-header { width: 70px; height: 70px; background: rgba(56, 189, 248, 0.1); color: #38bdf8; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 2rem; margin: 0 auto 20px; }
                .otp-input { background: #0f172a !important; border: 2px solid #334155 !important; color: white !important; height: 55px; font-size: 1.3rem; text-align: center; border-radius: 15px; }
                .otp-error-msg { color: #f87171; font-size: 0.85rem; background: rgba(248, 113, 113, 0.1); padding: 10px; border-radius: 10px; }
                .animate-fade-in { animation: fadeIn 0.4s ease; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
}